import { getAdder, getUnitStart, updateDateByPos } from "../time";
import { format, isToday, isSameMonth, startOfDay, endOfDay, endOfWeek, startOfWeek, differenceInCalendarDays, areIntervalsOverlapping, getWeekOfMonth, differenceInCalendarMonths, differenceInCalendarWeeks, addMinutes, isBefore, isSameDay, differenceInMinutes, } from "date-fns";
import { stackEvents, isMultiEvent, roundEvents } from "../events";
import { getFirstWritableCalendar, uid } from "./../helpers";
import { EventStore } from "./eventStore";
const addMonths = getAdder("month");
const addDays = getAdder("day");
function countEvents(start, end, events) {
    let count = 0;
    events.forEach(ev => {
        const { start_date, end_date } = ev;
        const overlay = areIntervalsOverlapping({ start, end }, { start: start_date, end: end_date });
        if (overlay)
            count++;
    });
    return count;
}
class MonthView {
    constructor() {
        this.getBounds = (date, config) => {
            const weekStartsOn = config?.weekStartsOn ?? 1;
            const startUnit = getUnitStart("month", date);
            const endUnit = addMonths(startUnit, 1);
            const start = startOfWeek(startUnit, { weekStartsOn });
            const end = endOfWeek(endUnit, { weekStartsOn });
            return [start, end];
        };
        this.getNext = (date) => {
            return addMonths(date, 1);
        };
        this.getPrev = (date) => {
            return addMonths(date, -1);
        };
        this.getWeekDays = (activeDate, config) => {
            const { weekStartsOn } = config;
            let dayOfWeek = startOfWeek(activeDate, { weekStartsOn });
            const weekDays = [];
            for (let i = 0; i < 7; i++) {
                weekDays.push({
                    label: dayOfWeek,
                });
                dayOfWeek = addDays(dayOfWeek, 1);
            }
            return weekDays;
        };
        this.getMonthTable = (activeDate, bounds, scaleModel) => {
            const { dayRows, dayCols, dayWidth } = scaleModel;
            const monthTable = [];
            let startWeek = bounds[0];
            for (let week = 0; week < dayRows; week++) {
                const endWeek = endOfDay(addDays(startWeek, dayCols - 1));
                const days = [];
                let date = startWeek;
                let dayOfWeek = 0;
                while (date.valueOf() <= endWeek.valueOf()) {
                    days.push({
                        value: date,
                        label: String(date.getDate()),
                        activeMonth: isSameMonth(activeDate, date),
                        today: isToday(date),
                        width: dayWidth,
                        col: dayOfWeek,
                        row: week,
                        events: 0,
                        showAll: false,
                    });
                    dayOfWeek++;
                    date = addDays(date, 1);
                }
                monthTable.push({
                    index: week,
                    start_date: startWeek,
                    end_date: endWeek,
                    days,
                    inDayEvents: new EventStore(),
                    multiDayEvents: new EventStore(),
                });
                startWeek = addDays(startWeek, dayCols);
            }
            return monthTable;
        };
        this.getEventFromPosition = (id, trigger, e, locale) => {
            let event;
            if (trigger === "data-cell") {
                const start_date = startOfDay(new Date(id));
                const end_date = endOfDay(start_date);
                const newevent = {
                    text: locale("New Event"),
                    start_date,
                    end_date,
                    dragMove: true,
                    id: uid(),
                };
                if (this._calendars.length) {
                    newevent.type = getFirstWritableCalendar(this._calendars);
                }
                event = this.updateEvents(newevent, true);
            }
            else {
                event = this._events.filter(i => i.eventData.id == id)[0];
                if (isMultiEvent(event.eventData)) {
                    const clickedDate = updateDateByPos(new Date(), e);
                    this._multieventDndDiff = differenceInCalendarDays(event.eventData.start_date, clickedDate);
                }
            }
            return event;
        };
        this.updateEventPosition = (dayEvent, e, _dragInfo, dragType) => {
            const { eventData } = dayEvent;
            this.updateEvents(eventData, false);
            if (dragType === "add") {
                const date = updateDateByPos(new Date(), e);
                const dayDiff = differenceInCalendarDays(date, eventData.start_date);
                if (dayDiff < 0)
                    return;
                eventData.end_date = dayDiff
                    ? startOfDay(addDays(date, 1))
                    : endOfDay(eventData.start_date);
            }
            else {
                const prevWeek = this._monthTable[this._getWeekIndex(eventData)];
                prevWeek.days.forEach(day => {
                    if (areIntervalsOverlapping({
                        start: eventData.start_date,
                        end: eventData.end_date,
                    }, {
                        start: startOfDay(day.value),
                        end: endOfDay(day.value),
                    })) {
                        day.events--;
                    }
                });
                const isMulti = isMultiEvent(eventData);
                if (dragType === "resize") {
                    document.body.style.cursor = "w-resize";
                    const start = startOfDay(eventData.end_date);
                    const end = updateDateByPos(eventData.end_date, e);
                    if (isBefore(end, eventData.start_date)) {
                        this.updateEvents(eventData, true);
                        return;
                    }
                    // add 1 day if end_date was 00:00
                    eventData.end_date = !differenceInMinutes(start, eventData.end_date)
                        ? addDays(end, 1)
                        : end;
                }
                else {
                    const minDiff = differenceInMinutes(eventData.end_date, eventData.start_date);
                    if (isMulti) {
                        eventData.start_date = addDays(updateDateByPos(eventData.start_date, e), this._multieventDndDiff);
                        eventData.end_date = addMinutes(eventData.start_date, minDiff);
                    }
                    else {
                        eventData.start_date = updateDateByPos(eventData.start_date, e);
                        eventData.end_date = addMinutes(eventData.start_date, minDiff);
                    }
                }
                const nextWeek = this._monthTable[this._getWeekIndex(eventData)];
                nextWeek.days.forEach(day => {
                    if (areIntervalsOverlapping({
                        start: eventData.start_date,
                        end: eventData.end_date,
                    }, {
                        start: startOfDay(day.value),
                        end: endOfDay(day.value),
                    })) {
                        day.events++;
                    }
                });
            }
            this.updateEvents(eventData, true);
        };
        this.getPositionInWeek = (date, dates) => {
            const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
            const day = dates.find(d => d.value.valueOf() === start.valueOf());
            return { col: day?.col || 0, row: day?.row || 0 };
        };
        this._getWeekIndex = (event) => {
            const { activeDate } = this._config;
            let weekIndex;
            if (isSameMonth(activeDate, event.start_date)) {
                weekIndex =
                    getWeekOfMonth(event.start_date, {
                        weekStartsOn: this._config.weekStartsOn,
                    }) - 1;
            }
            else {
                weekIndex =
                    differenceInCalendarMonths(event.start_date, activeDate) > 0
                        ? this._monthTable.length - 1
                        : 0;
            }
            return weekIndex;
        };
        this.normalizeEvent = (event, crossedEvents, week, hideEvent = false) => {
            const { dateHeight, dayWidth, eventHeight, eventVerticalSpace } = this._config;
            const pos = this.getPositionInWeek(event.start_date, week.days);
            let top = dateHeight;
            const left = `${event.start_date > week.start_date ? dayWidth * pos.col : 0}%`;
            const startDate = event.start_date < week.start_date
                ? week.start_date
                : event.start_date;
            // check if event end in 00.00 - set date minus 1 min
            const eventEndDate = isSameDay(event.end_date, addMinutes(event.end_date, -1))
                ? event.end_date
                : endOfDay(new Date(addMinutes(event.end_date, -1)));
            const endDate = eventEndDate < week.end_date ? eventEndDate : week.end_date;
            const width = `calc(${(differenceInCalendarDays(endDate, startDate) + 1) * dayWidth}% - 2px)`;
            const height = eventHeight + "px";
            const index = crossedEvents[event.id];
            if (index) {
                top = top + index * (eventHeight + eventVerticalSpace);
                if (top + eventHeight + eventVerticalSpace > week.height) {
                    week.height = top + eventHeight + eventVerticalSpace;
                }
            }
            return {
                eventData: event,
                position: {
                    top: top + "px",
                    left,
                    width,
                    height,
                    hideEvent,
                },
            };
        };
        this.getViewModel = (activeDate, bounds, config, displayedEvents, calendars) => {
            const days = differenceInCalendarDays(bounds[1], bounds[0]) + 1;
            const dayCols = 7; // number of days in week
            const dayRows = days / dayCols;
            const dayWidth = 100 / dayCols;
            this._config = {
                autoSave: false,
                weekStartsOn: 0,
                ...config,
                dayWidth: 100 / dayCols,
                dateHeight: 30,
                activeDate,
            };
            const { weekStartsOn, maxEventsPerCell, dateHeight, eventHeight, eventVerticalSpace, } = this._config;
            this._calendars = calendars;
            this._unsavedEvent = null;
            const weekDays = this.getWeekDays(activeDate, {
                weekStartsOn,
            });
            this._monthTable = this.getMonthTable(activeDate, bounds, {
                dayCols,
                dayRows,
                dayWidth,
                weekStartsOn,
            });
            this._events = [];
            this._init = [];
            this._monthTable.forEach(week => {
                let eventsInRow = displayedEvents.filter(ev => areIntervalsOverlapping({ start: week.start_date, end: week.end_date }, { start: ev.start_date, end: ev.end_date }));
                week.days.forEach(day => {
                    const start = startOfDay(day.value);
                    const end = endOfDay(day.value);
                    day.events = countEvents(start, end, eventsInRow);
                    let dayHeight = dateHeight +
                        day.events * (eventHeight + eventVerticalSpace);
                    if (day.events > maxEventsPerCell) {
                        const top = dateHeight +
                            maxEventsPerCell * (eventHeight + eventVerticalSpace) -
                            eventVerticalSpace;
                        const height = 25;
                        day.showAll = { top, height };
                        dayHeight = top + height;
                    }
                    if (!week.height || week.height < dayHeight) {
                        week.height = dayHeight;
                    }
                });
                const crossedEvents = this.getCrossedEvents(eventsInRow);
                eventsInRow = eventsInRow.map(event => this.normalizeEvent(event, crossedEvents, week, crossedEvents[event.id] >= maxEventsPerCell));
                const inDayEvents = [];
                const multiDayEvents = [];
                eventsInRow.forEach(day => {
                    this._events.push(day);
                    if (isMultiEvent(day.eventData)) {
                        multiDayEvents.push(day);
                    }
                    else {
                        inDayEvents.push(day);
                    }
                });
                this._init[week.index] = {
                    inDayEvents: inDayEvents,
                    multiDayEvents: multiDayEvents,
                };
            });
            this.resetToInit();
            return {
                title: this.getTitle.bind(this),
                calendars,
                weekDays,
                config: this._config,
                monthTable: this._monthTable,
                updateEventPosition: this.updateEventPosition.bind(this),
                getEventFromPosition: this.getEventFromPosition.bind(this),
                addEvent: this.addEvent.bind(this),
                removeEvent: this.removeEvent.bind(this),
                getEventById: this.getEventById.bind(this),
                resetToInit: this.resetToInit.bind(this),
                activeDate,
            };
        };
    }
    addEvent(event) {
        this.updateEvents(event, true);
    }
    removeEvent(id) {
        this._monthTable.forEach(w => {
            w.inDayEvents.remove(id);
            w.multiDayEvents.remove(id);
        });
    }
    getEventById(id) {
        return this._events.find(ev => ev.eventData.id === id)?.eventData;
    }
    updateEvents(event, add) {
        // update Events in weeks
        if (this._unsavedEvent && !this._config.autoSave) {
            this.removeEvent(this._unsavedEvent.id);
            this._unsavedEvent = null;
        }
        const firstDayOfView = this._monthTable[0].days[0].value;
        const diffInWeek = differenceInCalendarWeeks(addMinutes(event.end_date, -1), isBefore(event.start_date, firstDayOfView)
            ? firstDayOfView
            : event.start_date, { weekStartsOn: this._config.weekStartsOn });
        if (!add) {
            const prevWeekIndex = this._getWeekIndex(event);
            const isMulti = isMultiEvent(event);
            for (let i = 0; i <= diffInWeek; i++) {
                const week = this._monthTable[i + prevWeekIndex];
                if (!week)
                    break;
                const eventType = isMulti ? "multiDayEvents" : "inDayEvents";
                week[eventType].remove(event.id);
            }
            return;
        }
        const nextWeekIndex = this._getWeekIndex(event);
        let newday;
        const maxEventsPerCell = this._config.maxEventsPerCell;
        for (let i = 0; i <= diffInWeek; i++) {
            const week = this._monthTable[i + nextWeekIndex];
            if (!week)
                break;
            const weekEvents = [
                ...week.inDayEvents.get().map(ev => ev.eventData),
                ...week.multiDayEvents.get().map(ev => ev.eventData),
                event,
            ];
            const crossed = this.getCrossedEvents(weekEvents);
            newday = this.normalizeEvent(event, crossed, week);
            week[isMultiEvent(event) ? "multiDayEvents" : "inDayEvents"].add(newday);
            week.inDayEvents.set([
                ...week.inDayEvents
                    .get()
                    .map(ev => this.normalizeEvent(ev.eventData, crossed, week, crossed[ev.eventData.id] >= maxEventsPerCell)),
            ]);
            week.multiDayEvents.set([
                ...week.multiDayEvents
                    .get()
                    .map(ev => this.normalizeEvent(ev.eventData, crossed, week, crossed[ev.eventData.id] >= maxEventsPerCell)),
            ]);
            week.days;
        }
        this._monthTable = [...this._monthTable];
        this._unsavedEvent = event;
        return newday;
    }
    getCrossedEvents(events) {
        const round = roundEvents(events);
        return stackEvents(round);
    }
    resetToInit() {
        this._monthTable.forEach((week, index) => {
            week.inDayEvents.set(this._init[index].inDayEvents.map(ev => ({
                position: { ...ev.position },
                eventData: { ...ev.eventData },
            })));
            week.multiDayEvents.set(this._init[index].multiDayEvents.map(ev => ({
                position: { ...ev.position },
                eventData: { ...ev.eventData },
            })));
        });
    }
    getTitle(date, config) {
        const { dateTitle } = this._config;
        if (dateTitle)
            return dateTitle(date, this.getBounds(date, this._config));
        return format(date, "LLLL y", { locale: config.locale });
    }
}
export default MonthView;
//# sourceMappingURL=month.js.map